import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatDateTime } from "@/lib/format";
import {
  bedarfAnstehend,
  erfasseMerosZustand,
  getMerosUebersicht,
  legeMerosKlappTermin,
  loescheMerosKlappTermin,
  merosZustandLabel,
  speichereMerosFarben,
  type MerosBedarf,
  type MerosKlappTermin,
  type MerosUebersicht,
  type MerosZustand,
} from "@/lib/meros";
import { PLANUNG_COLORS } from "@/lib/montageplanung";
import { toast } from "@/lib/toast";
import { MerosZustandBadge } from "@/pages/veranstaltungen/MerosZustandBadge";

const FARBE_OBEN = "#0a84ff";
const FARBE_UNTEN = "#8e8e93";

function jetztLokal(): string {
  const date = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function ortZeile(item: MerosBedarf): string {
  const parts = [item.bereich, item.ort, item.venue]
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.filter((part, index) => parts.indexOf(part) === index).join(" · ");
}

function farbeFuer(farben: { oben: string; unten: string } | undefined, zustand: MerosZustand | null | undefined) {
  if (zustand === "oben") return farben?.oben || FARBE_OBEN;
  if (zustand === "unten") return farben?.unten || FARBE_UNTEN;
  return "#8e8e93";
}

function FarbPunkt({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-label">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} aria-hidden />
      {label}
    </span>
  );
}

function FarbeWahl({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (color: string) => void;
}) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">{label}</p>
      <div className="mt-2 flex flex-wrap gap-1.5" role="radiogroup" aria-label={label}>
        {PLANUNG_COLORS.map((color) => {
          const selected = value.toLowerCase() === color.value;
          return (
            <button
              key={color.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={color.label}
              title={color.label}
              disabled={disabled}
              className={clsx(
                "h-7 w-7 rounded-full ring-2 ring-offset-2 ring-offset-card transition disabled:opacity-50",
                selected ? "ring-label" : "ring-transparent hover:ring-separator",
              )}
              style={{ backgroundColor: color.value }}
              onClick={() => onChange(color.value)}
            />
          );
        })}
      </div>
    </div>
  );
}

function StatusKachel({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <article className="rounded-ios-lg bg-card px-4 py-4 shadow-ios ring-1 ring-separator/40">
      <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">{label}</p>
      <div className="mt-2">{children}</div>
    </article>
  );
}

function BedarfListe({
  items,
  aktuell,
  farben,
}: {
  items: MerosBedarf[];
  aktuell: MerosZustand | null;
  farben: { oben: string; unten: string } | undefined;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-label-secondary">Keine Einträge.</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((item, index) => {
        const abweichung = Boolean(aktuell && item.zustand && item.zustand !== aktuell);
        return (
          <li
            key={`${item.mail_id}-${item.datum}-${index}`}
            className="rounded-ios bg-fill-quaternary/25 px-3 py-3 ring-1 ring-separator/30"
          >
            <div className="flex items-start gap-3">
              <span
                className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: farbeFuer(farben, item.zustand) }}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="font-medium text-label">{item.event_title || "Veranstaltung"}</p>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <MerosZustandBadge zustand={item.zustand} />
                    {abweichung ? (
                      <span className="text-xs font-medium text-status-orange">Abweichung</span>
                    ) : null}
                  </div>
                </div>
                <p className="mt-0.5 text-sm text-label-secondary">
                  {[item.datum || item.event_date, ortZeile(item), item.kategorie].filter(Boolean).join(" · ") ||
                    "Ohne Datum"}
                </p>
                {item.aufgabe ? <p className="mt-1 text-sm text-label">{item.aufgabe}</p> : null}
                {item.notiz ? (
                  <p className="mt-1 whitespace-pre-line text-xs leading-5 text-label-secondary">{item.notiz}</p>
                ) : null}
                {!item.zustand ? (
                  <p className="mt-1 text-xs text-label-tertiary">Richtung unklar</p>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function KlappZeile({
  termin,
  farben,
  disabled,
  onDelete,
}: {
  termin: MerosKlappTermin;
  farben: { oben: string; unten: string } | undefined;
  disabled: boolean;
  onDelete: () => void;
}) {
  const vorbei = new Date(termin.klapp_am).getTime() < Date.now();
  return (
    <li className={clsx("flex items-start justify-between gap-3 py-3", vorbei && "opacity-60")}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-label">{formatDateTime(termin.klapp_am)}</p>
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-label-secondary">
          {termin.zustand_davor && termin.zustand_danach ? (
            <>
              <FarbPunkt color={farbeFuer(farben, termin.zustand_davor)} label={merosZustandLabel(termin.zustand_davor)} />
              <span aria-hidden>→</span>
              <FarbPunkt color={farbeFuer(farben, termin.zustand_danach)} label={merosZustandLabel(termin.zustand_danach)} />
            </>
          ) : (
            <span className="text-xs">Vorheriger Zustand fehlt noch</span>
          )}
        </p>
        {termin.notiz ? <p className="mt-1 text-sm text-label-secondary">{termin.notiz}</p> : null}
      </div>
      <Button size="sm" variant="ghost" disabled={disabled} onClick={onDelete}>
        Löschen
      </Button>
    </li>
  );
}

export function MerosPage() {
  const [data, setData] = useState<MerosUebersicht | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zustand, setZustand] = useState<MerosZustand | "">("");
  const [erfasstAm, setErfasstAm] = useState(jetztLokal);
  const [notiz, setNotiz] = useState("");
  const [klappAm, setKlappAm] = useState(jetztLokal);
  const [klappNotiz, setKlappNotiz] = useState("");
  const [klappSaving, setKlappSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getMerosUebersicht());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Der Merostützen-Zustand konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const anstehend = useMemo(
    () => (data?.bedarf || []).filter((item) => bedarfAnstehend(item)),
    [data],
  );
  const vergangen = useMemo(
    () => (data?.bedarf || []).filter((item) => !bedarfAnstehend(item)).reverse(),
    [data],
  );
  const aktuell = data?.aktuell?.zustand || null;
  const kommendeKlapps = useMemo(
    () => (data?.klapp_termine || []).filter((termin) => new Date(termin.klapp_am).getTime() >= Date.now()),
    [data],
  );
  const vergangeneKlapps = useMemo(
    () =>
      (data?.klapp_termine || [])
        .filter((termin) => new Date(termin.klapp_am).getTime() < Date.now())
        .reverse(),
    [data],
  );
  const naechsterKlapp = kommendeKlapps[0];
  const abweichungen = anstehend.filter((item) => aktuell && item.zustand && item.zustand !== aktuell).length;
  const farben = {
    oben: data?.farben?.oben || FARBE_OBEN,
    unten: data?.farben?.unten || FARBE_UNTEN,
  };

  async function handleSave() {
    if (zustand !== "oben" && zustand !== "unten") return;
    const when = new Date(erfasstAm);
    if (Number.isNaN(when.getTime())) {
      toast.error("Bitte einen gültigen Zeitpunkt angeben.");
      return;
    }
    setSaving(true);
    try {
      const note = notiz.trim();
      const next = await erfasseMerosZustand({
        zustand,
        erfasst_am: when.toISOString(),
        notiz: note || undefined,
      });
      setData(next);
      setNotiz("");
      setErfasstAm(jetztLokal());
      setError(null);
      toast.success(`Zustand ${merosZustandLabel(zustand)} erfasst.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Der Zustand konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  async function handleKlapp() {
    const when = new Date(klappAm);
    if (Number.isNaN(when.getTime())) {
      toast.error("Bitte einen gültigen Zeitpunkt angeben.");
      return;
    }
    setKlappSaving(true);
    try {
      const note = klappNotiz.trim();
      const next = await legeMerosKlappTermin({
        klapp_am: when.toISOString(),
        notiz: note || undefined,
      });
      setData(next);
      setKlappNotiz("");
      setKlappAm(jetztLokal());
      setError(null);
      const angelegt = next.klapp_termine.find(
        (termin) => Math.abs(new Date(termin.klapp_am).getTime() - when.getTime()) < 60_000,
      );
      const danach = angelegt?.zustand_danach;
      toast.success(
        danach
          ? `Klappen ist als Einsatz eingetragen. Danach stehen die Merostützen ${merosZustandLabel(danach)}.`
          : "Klappen ist als Einsatz eingetragen. Dafür fehlt noch ein vorheriger Zustand.",
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Der Termin konnte nicht gespeichert werden.");
    } finally {
      setKlappSaving(false);
    }
  }

  async function handleFarben(oben: string, unten: string) {
    setKlappSaving(true);
    try {
      setData(await speichereMerosFarben({ oben, unten }));
      setError(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Die Farben konnten nicht gespeichert werden.");
    } finally {
      setKlappSaving(false);
    }
  }

  async function handleKlappLoeschen(id: number) {
    if (!window.confirm("Diesen Klapp-Termin wirklich löschen?")) return;
    setKlappSaving(true);
    try {
      setData(await loescheMerosKlappTermin(id));
      setError(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Der Termin konnte nicht gelöscht werden.");
    } finally {
      setKlappSaving(false);
    }
  }

  const ausgang = data?.historie.length ? data.historie[data.historie.length - 1] : undefined;

  return (
    <AppShell title="Meros" subtitle="Stand der Merostützen, das nächste Klappen und der Bedarf aus den Veranstaltungen">
      <div className="space-y-4">
        {error ? <Alert variant="warning">{error}</Alert> : null}

        {loading && !data ? (
          <div className="flex justify-center py-16">
            <Spinner className="h-6 w-6" />
          </div>
        ) : (
          <>
            <section className="grid gap-3 md:grid-cols-3" aria-label="Überblick">
              <StatusKachel label="Aktueller Zustand">
                {data?.aktuell ? (
                  <>
                    <p className="flex items-center gap-2 text-2xl font-semibold tracking-wide text-label">
                      <span
                        className="h-3.5 w-3.5 rounded-full"
                        style={{ backgroundColor: farbeFuer(farben, data.aktuell.zustand) }}
                        aria-hidden
                      />
                      {merosZustandLabel(data.aktuell.zustand)}
                    </p>
                    <p className="mt-2 text-sm text-label-secondary">
                      {data.aktuell.art === "klapp" ? "Seit dem Klappen " : "Seit "}
                      {data.aktuell.seit ? formatDateTime(data.aktuell.seit) : "—"}
                      {data.aktuell.erfasst_von ? ` · ${data.aktuell.erfasst_von}` : ""}
                    </p>
                    {data.aktuell.notiz ? (
                      <p className="mt-2 whitespace-pre-line text-sm text-label">{data.aktuell.notiz}</p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-label-secondary">Noch kein Ausgangszustand erfasst.</p>
                )}
              </StatusKachel>

              <StatusKachel label="Nächstes Klappen">
                {naechsterKlapp ? (
                  <>
                    <p className="text-sm font-medium text-label">{formatDateTime(naechsterKlapp.klapp_am)}</p>
                    <p className="mt-2 flex flex-wrap items-center gap-1.5">
                      {naechsterKlapp.zustand_davor && naechsterKlapp.zustand_danach ? (
                        <>
                          <FarbPunkt
                            color={farbeFuer(farben, naechsterKlapp.zustand_davor)}
                            label={merosZustandLabel(naechsterKlapp.zustand_davor)}
                          />
                          <span className="text-label-tertiary" aria-hidden>
                            →
                          </span>
                          <FarbPunkt
                            color={farbeFuer(farben, naechsterKlapp.zustand_danach)}
                            label={merosZustandLabel(naechsterKlapp.zustand_danach)}
                          />
                        </>
                      ) : (
                        <span className="text-sm text-label-secondary">Vorheriger Zustand fehlt noch.</span>
                      )}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-label-secondary">Kein Termin eingetragen.</p>
                )}
              </StatusKachel>

              <StatusKachel label="Veranstaltungen">
                <p className="text-sm font-medium text-label">
                  {anstehend.length === 0
                    ? "Nichts anstehend"
                    : anstehend.length === 1
                      ? "1 anstehend"
                      : `${anstehend.length} anstehend`}
                </p>
                {anstehend.length > 0 ? (
                  <p className={clsx("mt-2 text-sm", abweichungen > 0 ? "text-status-orange" : "text-label-secondary")}>
                    {aktuell
                      ? abweichungen === 0
                        ? "Alle passen zum aktuellen Stand."
                        : abweichungen === 1
                          ? "1 weicht vom aktuellen Stand ab."
                          : `${abweichungen} weichen vom aktuellen Stand ab.`
                      : "Ohne Ausgangszustand kein Vergleich."}
                  </p>
                ) : null}
              </StatusKachel>
            </section>

            {data && data.historie.length === 0 ? (
              <Card
                title="Ausgangszustand"
                description="Einmal eintragen. Danach ergibt sich der Stand aus den Klapp-Terminen."
              >
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Zustand">
                    {(["oben", "unten"] as const).map((value) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={zustand === value}
                        className={clsx(
                          "rounded-lg px-4 py-2 text-sm font-semibold ring-1 ring-inset transition",
                          zustand === value
                            ? "bg-accent text-label-on-accent ring-accent"
                            : "bg-card text-label ring-separator hover:bg-fill-tertiary",
                        )}
                        onClick={() => setZustand(value)}
                      >
                        {merosZustandLabel(value)}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="text-label-secondary">Gilt seit</span>
                      <input
                        type="datetime-local"
                        className="eh-input"
                        value={erfasstAm}
                        onChange={(event) => setErfasstAm(event.target.value)}
                      />
                    </label>
                    <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-sm">
                      <span className="text-label-secondary">Notiz</span>
                      <input
                        type="text"
                        maxLength={500}
                        className="eh-input"
                        value={notiz}
                        placeholder="optional"
                        onChange={(event) => setNotiz(event.target.value)}
                      />
                    </label>
                  </div>
                  <Button onClick={() => void handleSave()} loading={saving} disabled={zustand === ""}>
                    Zustand erfassen
                  </Button>
                </div>
              </Card>
            ) : null}

            <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(18rem,0.9fr)]">
              <Card
                title="Benötigt"
                description="Aus den aktuellen Regieplänen, im Montageplan als Einsatz in dieser Farbe."
                actions={
                  <Link to="/veranstaltungen/messe" className="text-sm font-medium text-accent hover:underline">
                    Veranstaltungen
                  </Link>
                }
              >
                {anstehend.length === 0 ? (
                  <p className="text-sm text-label-secondary">
                    In den gelesenen Veranstaltungen ist noch kein Merostützen-Zustand genannt.
                  </p>
                ) : (
                  <BedarfListe items={anstehend} aktuell={aktuell} farben={farben} />
                )}
                {vergangen.length > 0 ? (
                  <details className="mt-4 border-t border-separator pt-3">
                    <summary className="cursor-pointer text-sm font-medium text-label-secondary">
                      Vergangen ({vergangen.length})
                    </summary>
                    <div className="pt-3">
                      <BedarfListe items={vergangen} aktuell={null} farben={farben} />
                    </div>
                  </details>
                ) : null}
              </Card>

              <div className="space-y-4">
                <Card title="Farben" description="Oben und unten, für Zustand, Klappen und Veranstaltungen.">
                  <div className="space-y-4">
                    <FarbeWahl
                      label="Oben"
                      value={farben.oben}
                      disabled={klappSaving}
                      onChange={(color) => void handleFarben(color, farben.unten)}
                    />
                    <FarbeWahl
                      label="Unten"
                      value={farben.unten}
                      disabled={klappSaving}
                      onChange={(color) => void handleFarben(farben.oben, color)}
                    />
                  </div>
                </Card>

                <Card
                  title="Klappen"
                  description="Dreht den Zustand um. Der neue Stand beginnt am nächsten Tag."
                >
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-3">
                      <label className="flex flex-col gap-1 text-sm">
                        <span className="text-label-secondary">Klappen am</span>
                        <input
                          type="datetime-local"
                          className="eh-input"
                          value={klappAm}
                          onChange={(event) => setKlappAm(event.target.value)}
                        />
                      </label>
                      <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-sm">
                        <span className="text-label-secondary">Notiz</span>
                        <input
                          type="text"
                          maxLength={500}
                          className="eh-input"
                          value={klappNotiz}
                          placeholder="optional"
                          onChange={(event) => setKlappNotiz(event.target.value)}
                        />
                      </label>
                    </div>
                    <Button onClick={() => void handleKlapp()} loading={klappSaving}>
                      Termin eintragen
                    </Button>
                    {kommendeKlapps.length === 0 ? (
                      <p className="text-sm text-label-secondary">Kein anstehender Termin.</p>
                    ) : (
                      <ul className="divide-y divide-separator">
                        {kommendeKlapps.map((termin) => (
                          <KlappZeile
                            key={termin.id}
                            termin={termin}
                            farben={farben}
                            disabled={klappSaving}
                            onDelete={() => void handleKlappLoeschen(termin.id)}
                          />
                        ))}
                      </ul>
                    )}
                    {vergangeneKlapps.length > 0 ? (
                      <details className="border-t border-separator pt-3">
                        <summary className="cursor-pointer text-sm font-medium text-label-secondary">
                          Vergangene Termine ({vergangeneKlapps.length})
                        </summary>
                        <ul className="divide-y divide-separator">
                          {vergangeneKlapps.map((termin) => (
                            <KlappZeile
                              key={termin.id}
                              termin={termin}
                              farben={farben}
                              disabled={klappSaving}
                              onDelete={() => void handleKlappLoeschen(termin.id)}
                            />
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </div>
                </Card>
              </div>
            </div>

            {ausgang ? (
              <details className="rounded-ios-lg bg-card px-5 py-3 shadow-ios ring-1 ring-separator/40">
                <summary className="cursor-pointer text-sm font-medium text-label">
                  {data && data.historie.length > 1
                    ? `Erfassungen (${data.historie.length})`
                    : `Ausgangszustand ${merosZustandLabel(ausgang.zustand)}${ausgang.erfasst_am ? ` · ${formatDateTime(ausgang.erfasst_am)}` : ""}`}
                </summary>
                <ul className="mt-3 divide-y divide-separator">
                  {data?.historie.map((entry) => (
                    <li key={entry.id} className="flex flex-wrap items-start justify-between gap-3 py-3 first:pt-0">
                      <div>
                        <MerosZustandBadge zustand={entry.zustand} prefix="" />
                        {entry.notiz ? <p className="mt-2 text-sm text-label">{entry.notiz}</p> : null}
                      </div>
                      <p className="text-sm text-label-secondary">
                        {entry.erfasst_am ? formatDateTime(entry.erfasst_am) : "—"}
                        {entry.erfasst_von ? ` · ${entry.erfasst_von}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </>
        )}
      </div>
    </AppShell>
  );
}
